import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Collapse, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useRetention } from '../RetentionContext';
import { EmptyState, IconBadge, InfoList, SoftChip } from '../components/ui';
import { RT } from '../theme/retentionTheme';
import CourseEnrollment from '../../cro/myworkspace/CROleads/CourseEnroll';
import TutorMatchPanel from '../../crd/myworkspace/tutorCards/TutorMatchPanel';
import { useDispatch, useSelector } from 'react-redux';
import { createDemoAttempt } from '../../../redux/features/retentionSlice';
import { getTrainerData } from '../../../redux/features/CRDslice';
import useDemoData, { openAttempt } from './useDemoData';
import TutorStatusList, { TUTOR_STATUS } from './TutorStatusList';

// Stage 4 · Tutor mapping, per subject (RETENTION_WORKFLOW.md §5.5 stage 4):
//   1. no subject yet → the existing Enroll Subject dialog
//   2. no session preference → the existing Session Preference page, which returns here after a successful add
//   3. two ways to pick the tutor, both ending in the existing assign-trainer + POST /retention/:id/demo-attempt:
//      · Match making → the CRD matching cards (TutorMatchPanel) inline, pre-selected on this subject
//      · Tutor list → the admission register's Assign Tutor page (/my-workspace/assign-trainer), which comes back
//        here after the assign; the attempt is recorded on return
//   A tutor assigned outside the workspace (CRD page, admission register) is recorded with "Use this tutor".
//   Each subject also lists its tutors with the admission register's Change Status (Active / Temporary Inactive /
//   Inactive) - the same dialog and API as that page (TutorStatusList).

function SubjectRow({ subject, attempts, onPreferences, onTutorList, busy, onRecord, tutorRows, statusParams, onStatusChanged }) {
  const [searching, setSearching] = useState(false);
  const confirmed = openAttempt(subject);
  const latest = subject.latest_attempt;
  const hasPrefs = subject.preferences.length > 0;
  // an active tutor the workspace hasn't recorded yet (assigned from the CRD page, or recording failed after assign)
  const unrecorded = subject.trainer_assignment_id && Number(confirmed?.trainer_assignment_id) !== Number(subject.trainer_assignment_id);
  const remapping = latest && Number(latest.mapping_status) === 0 && latest.attempt_no > 1;
  // the recorded tutor was set Temporary Inactive / Inactive through Change Status
  const confirmedRow = confirmed && tutorRows.find((r) => Number(r.trainer_assignment_id) === Number(confirmed.trainer_assignment_id));
  const confirmedOff = confirmedRow && Number(confirmedRow.mapping_status) !== 1 ? TUTOR_STATUS[Number(confirmedRow.mapping_status)] : null;
  // current tutor set Inactive (not Temporary Inactive, which is a pause): offer the tutor assign options again. Recording
  // the new tutor closes this attempt and opens the next (POST /retention/:id/demo-attempt), the old tutor as previous.
  const canReassign = Boolean(confirmedRow) && Number(confirmedRow.mapping_status) === 0;

  const assignButtons = (
    <>
      <Tooltip title="Tutors ranked by preference, slots and response">
        <Button
          size="small"
          variant={searching ? 'outlined' : 'contained'}
          startIcon={<Icon icon={searching ? 'solar:close-circle-bold' : 'solar:magic-stick-3-bold'} width={16} />}
          onClick={() => setSearching((s) => !s)}
        >
          {searching ? 'Close' : 'Match making'}
        </Button>
      </Tooltip>
      <Tooltip title="All tutors — the admission register's tutor mapping">
        <Button
          size="small"
          variant="outlined"
          startIcon={<Icon icon="solar:list-check-bold" width={16} />}
          onClick={() => onTutorList(subject)}
        >
          Tutor list
        </Button>
      </Tooltip>
    </>
  );
  const excludeTrainerIds = useMemo(
    () => attempts.filter((a) => a.subject_enrollment_id === subject.subject_enrollment_id && a.matched_trainer_id).map((a) => a.matched_trainer_id),
    [attempts, subject.subject_enrollment_id],
  );

  return (
    <Box sx={{ p: 1.75, borderRadius: 3, border: `1px solid ${confirmed ? RT.success : RT.line}` }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" gap={1.25} alignItems="center" sx={{ minWidth: 180 }}>
          <IconBadge icon="solar:book-2-bold-duotone" color={confirmed ? RT.success : RT.primary} size={36} />
          <Box>
            <Typography variant="subtitle2">{subject.subject_name}</Typography>
            <Typography variant="caption" sx={{ color: RT.muted }}>
              {subject.total_sessions || '—'} sessions · {subject.cro_class_per_week || '—'}/week
            </Typography>
          </Box>
        </Stack>

        {/* preference */}
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
          {hasPrefs ? (
            <>
              <SoftChip label={subject.preferred_days?.replace(/,/g, ' · ')} icon="solar:calendar-bold" color={RT.info} />
              <SoftChip label={subject.preferred_time_slot} icon="solar:clock-circle-bold" color={RT.info} />
              <Button size="small" onClick={onPreferences} sx={{ minWidth: 0 }}>
                Edit
              </Button>
            </>
          ) : (
            <SoftChip label="No session preference" icon="solar:danger-triangle-bold" color={RT.warning} />
          )}
        </Stack>

        {/* tutor */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          {remapping && <SoftChip label={`Remap · attempt ${latest.attempt_no}`} icon="solar:refresh-bold" color={RT.warning} />}
          {confirmed && !unrecorded && confirmedOff ? (
            <>
              <SoftChip
                label={`${confirmed.trainer_name || 'Tutor'} · ${confirmedOff.label}`}
                icon={confirmedOff.icon}
                color={confirmedOff.color}
              />
              {canReassign && assignButtons}
            </>
          ) : confirmed && !unrecorded ? (
            <SoftChip label={`${confirmed.trainer_name || 'Tutor'} · attempt ${confirmed.attempt_no}`} icon="solar:check-circle-bold" color={RT.success} />
          ) : unrecorded ? (
            <>
              <SoftChip label={subject.trainer_name || 'Assigned tutor'} icon="solar:user-check-bold" color={RT.ink2} />
              <Button size="small" variant="contained" disabled={busy} onClick={() => onRecord(subject.subject_enrollment_id)}>
                Use this tutor
              </Button>
            </>
          ) : !hasPrefs ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<Icon icon="solar:calendar-add-bold" width={16} />}
              onClick={onPreferences}
            >
              Add preference
            </Button>
          ) : (
            assignButtons
          )}
        </Stack>
      </Stack>

      <TutorStatusList rows={tutorRows} dispatchParams={statusParams} onChanged={onStatusChanged} />

      <Collapse in={searching && (!confirmed || canReassign)} unmountOnExit>
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${RT.line}` }}>
          <TutorMatchPanel
            courseEnrollmentId={subject.course_enrollment_id}
            subjectEnrollmentId={subject.subject_enrollment_id}
            excludeTrainerIds={excludeTrainerIds}
            onAssigned={async () => {
              if (await onRecord(subject.subject_enrollment_id)) setSearching(false);
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default function TutorMappingStage() {
  const { student } = useRetention();
  const { d, demo, saving, reload, run, courseEnrollmentId } = useDemoData();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // every tutor assignment of this enrolment with its status - the admission register's Tutor Mapping list, unchanged.
  // Re-read whenever the demo data reloads (an assign, a record, a status change).
  const { trainerData } = useSelector((state) => state.crd);
  const statusParams = useMemo(
    () => ({ paginationData: { pageNum: 1, gridSize: 100, search: '' }, courseEnrollmentIdFromQuery: courseEnrollmentId }),
    [courseEnrollmentId],
  );
  useEffect(() => {
    if (courseEnrollmentId) dispatch(getTrainerData(statusParams));
  }, [dispatch, statusParams, courseEnrollmentId, d]);
  const tutorRows = (Array.isArray(trainerData?.data) ? trainerData.data : []).filter((r) => r.trainer_assignment_id);

  // the existing Session Preference page; returnTo brings the CRO back here after a successful add
  const prefUrl = useMemo(() => {
    const q = new URLSearchParams({
      courseenrollmentId: courseEnrollmentId,
      studentname: student?.name || '',
      course: student?.assigned_course_name || '',
      phone: student?.contactNumber || '',
      returnTo: `${location.pathname}${location.search}`,
    });
    return `/my-workspace/session-preference?${q}`;
  }, [courseEnrollmentId, student, location.pathname, location.search]);
  const goToPreferences = () => navigate(prefUrl);
  // the admission register's tutor list (same page its "Assign" button opens), returning here after the assign
  const goToTutorList = (subject) =>
    navigate('/my-workspace/assign-trainer', {
      state: {
        subjectEnrollmentId: subject.subject_enrollment_id,
        subjectName: `${student?.name || 'Student'} · ${subject.subject_name}`,
        returnTo: `${location.pathname}${location.search}`,
      },
    });

  // back from the tutor list after an assign: record the attempt once, then drop the flag from history
  const assignedSubject = location.state?.assignedSubjectEnrollmentId;
  const recordedRef = useRef(null);
  useEffect(() => {
    if (!assignedSubject || recordedRef.current === assignedSubject) return;
    recordedRef.current = assignedSubject;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    run(createDemoAttempt({ courseEnrollmentId, subject_enrollment_id: assignedSubject }), 'Tutor mapping recorded');
  }, [assignedSubject, courseEnrollmentId, location.pathname, location.search, navigate, run]);

  if (demo.error) return <Alert severity="info">{demo.error.message}</Alert>;
  if (!d) return <EmptyState icon="svg-spinners:ring-resize" title="Loading…" compact />;

  const subjects = d.subjects.map((s) => ({ ...s, course_enrollment_id: courseEnrollmentId }));
  const record = (subjectEnrollmentId) =>
    run(createDemoAttempt({ courseEnrollmentId, subject_enrollment_id: subjectEnrollmentId }), 'Tutor mapping recorded');

  const enroll = (
    <CourseEnrollment
      courseenrollmentId={courseEnrollmentId}
      canEnroll
      FeePersession={student?.sroFeePerSession}
      onEnrolled={reload}
      renderTrigger={(open) => (
        <Button size="small" variant={subjects.length ? 'outlined' : 'contained'} startIcon={<Icon icon="solar:add-square-bold" width={16} />} onClick={open}>
          Enroll Subject
        </Button>
      )}
    />
  );

  return (
    <Stack gap={2}>
      <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: RT.surface }}>
        <InfoList
          rows={[
            { icon: 'solar:user-bold', label: 'Tutor gender (CRM)', value: d.lead?.gender_preference },
            { icon: 'solar:chat-round-dots-bold', label: 'Languages (CRM)', value: d.lead?.teaching_language },
          ]}
        />
      </Box>

      {subjects.length === 0 ? (
        <Stack alignItems="center" gap={1}>
          <EmptyState icon="solar:book-bookmark-linear" title="No subjects enrolled" compact />
          {enroll}
        </Stack>
      ) : (
        <Stack gap={1.25}>
          {subjects.map((s) => (
            <SubjectRow
              key={s.subject_enrollment_id}
              subject={s}
              attempts={d.attempts}
              onPreferences={goToPreferences}
              onTutorList={goToTutorList}
              busy={saving}
              onRecord={record}
              tutorRows={tutorRows.filter((r) => Number(r.subject_enrollment_id) === Number(s.subject_enrollment_id))}
              statusParams={statusParams}
              onStatusChanged={reload}
            />
          ))}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Tooltip title="Add or edit days and time slots">
              <Button size="small" variant="outlined" startIcon={<Icon icon="solar:calendar-bold" width={16} />} onClick={goToPreferences}>
                Session preferences
              </Button>
            </Tooltip>
            {enroll}
          </Stack>
        </Stack>
      )}
      {subjects.length > 0 && (
        <Typography variant="caption" sx={{ color: RT.muted }}>
          {subjects.filter(openAttempt).length}/{subjects.length} subjects mapped
        </Typography>
      )}
    </Stack>
  );
}
